<template>
  <div v-if="enabled" class="notification-bell">
    <v-menu v-model="open" location="bottom end" :offset="8" :close-on-content-click="false">
      <template #activator="{ props }">
        <v-btn v-bind="props" icon variant="text" size="small" class="mr-2" title="Bildirimler">
          <v-badge :content="badgeText" :model-value="unread > 0" color="error" floating>
            <v-icon>mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <v-card min-width="340" max-width="400">
        <v-card-title class="d-flex align-center text-subtitle-1">
          Bildirimler
          <v-spacer></v-spacer>
          <v-btn size="x-small" variant="text" :disabled="!unread" @click="markAll">Tümünü okundu yap</v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <div v-if="loading" class="pa-6 text-center"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
        <v-list v-else-if="items.length" density="compact" class="bell-list">
          <v-list-item v-for="n in items" :key="n.id" :class="{ 'bell-unread': !n.isRead }" @click="openItem(n)">
            <template #prepend>
              <v-icon :color="meta(n.type).color" size="20">{{ meta(n.type).icon }}</v-icon>
            </template>
            <v-list-item-title :class="{ 'font-weight-bold': !n.isRead }">{{ n.title }}</v-list-item-title>
            <v-list-item-subtitle class="bell-msg">{{ n.message }}</v-list-item-subtitle>
            <div class="text-caption text-medium-emphasis">{{ formatDate(n.createdAt) }}</div>
          </v-list-item>
        </v-list>
        <div v-else class="pa-6 text-center text-medium-emphasis">Bildirim yok</div>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn block variant="text" color="primary" @click="goList">Tümünü gör</v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <v-snackbar v-model="toast.show" :color="toast.color" location="top right" :timeout="8000" multi-line>
      <div class="font-weight-bold">{{ toast.title }}</div>
      <div class="text-caption">{{ toast.message }}</div>
      <template #actions>
        <v-btn variant="text" @click="toast.show = false">Kapat</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { useUserStore } from "@/stores/UserStore";
import { acquireHub, subscribeHub, onHubResync } from "@/utils/notificationHub";
import {
  NOTIFICATIONS_CHANGED_EVENT,
  getMyNotificationsService,
  getUnreadNotificationCountService,
  markAllNotificationsReadService,
  markNotificationReadService,
} from "@/utils/NotificationService";
import { getNotificationMeta, getTokenClaims, isOwnerClaims } from "@/utils/notificationMeta";
import { formatIstanbul } from "@/utils/PanicAlertService";

// Yalnızca araç sahibi girişinde görünür; hub bağlantısını sekme boyunca paylaşır.
export default {
  name: "NotificationBell",
  data() {
    return {
      open: false,
      loading: false,
      unread: 0,
      items: [],
      toast: { show: false, title: "", message: "", color: "info" },
      releaseHub: null,
      offs: [],
    };
  },
  computed: {
    token() {
      return useUserStore().getToken;
    },
    enabled() {
      return isOwnerClaims(getTokenClaims(this.token));
    },
    badgeText() {
      return this.unread > 99 ? "99+" : this.unread;
    },
  },
  watch: {
    enabled: {
      immediate: true,
      handler(val) {
        val ? this.start() : this.stop();
      },
    },
    open(val) {
      if (val) this.loadItems();
    },
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    meta: getNotificationMeta,
    formatDate: formatIstanbul,
    start() {
      if (this.releaseHub) return;
      this.releaseHub = acquireHub();
      this.offs = [
        subscribeHub("ReceiveNotification", this.onLive),
        onHubResync(() => {
          this.loadCount();
          if (this.open) this.loadItems();
        }),
      ];
      window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, this.loadCount);
      this.loadCount();
    },
    stop() {
      this.offs.forEach((off) => off());
      this.offs = [];
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, this.loadCount);
      if (this.releaseHub) this.releaseHub();
      this.releaseHub = null;
    },
    async loadCount() {
      const res = await getUnreadNotificationCountService();
      if (res.status) this.unread = res.data?.count ?? 0;
    },
    async loadItems() {
      this.loading = true;
      const res = await getMyNotificationsService({ page: 1, pageSize: 10 });
      if (res.status) this.items = res.data?.items || [];
      this.loading = false;
    },
    onLive(n) {
      if (this.items.some((i) => i.id === n.id)) return; // aynı id tekrar eklenmez
      this.items = [n, ...this.items].slice(0, 10);
      if (!n.isRead) this.unread += 1;
      const meta = getNotificationMeta(n.type);
      this.toast = { show: true, title: n.title, message: n.message, color: meta.color };
    },
    async openItem(n) {
      if (!n.isRead) {
        const res = await markNotificationReadService(n.id);
        if (res.status) {
          n.isRead = true;
          this.unread = Math.max(0, this.unread - 1);
        }
      }
    },
    async markAll() {
      const res = await markAllNotificationsReadService();
      if (res.status) {
        this.items.forEach((i) => (i.isRead = true));
        this.unread = 0;
        window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED_EVENT));
      }
    },
    goList() {
      this.open = false;
      this.$router.push("/notifications");
    },
  },
};
</script>

<style scoped>
.bell-list {
  max-height: 360px;
  overflow-y: auto;
}
.bell-unread {
  background: #f1f6ff;
}
.bell-msg {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
